import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { HedgeTradesPanel } from './hedgeTradesPanel';
import { decimalInput, formatMoney, formatNumber, localIsoDate } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { Breadcrumb } from '../shared/Breadcrumb';
import { CoverageDayView } from './coverageDayView';

type Locale = 'hr' | 'en';

type Signal = {
    band_pct: number;
    deviation_pct: number | null;
    status: 'green' | 'red' | 'unknown';
    direction: 'shortage' | 'surplus' | 'flat';
    avg_daily_order_eur: number;
    window_days: number;
    days: number | null;
};

type Row = {
    key: string;
    label: string;
    group: 'coverage' | 'lager' | 'result' | 'custody' | 'vault' | 'hedge';
    in_z1: boolean;
    in_z2?: boolean;
    qty: number;
    fine_g: number;
    book_eur: number;
    spot_eur: number;
    note: string | null;
    signal?: Signal;
    order_count?: number;
    remaining_eur?: number;
    prev_day_book_eur?: number | null;
    pending?: boolean;
    entry_date?: string | null;
    entered_by?: string | null;
    age_days?: number | null;
    stale?: boolean;
};

type HistoryPoint = { date: string; qty: number; book_eur: number; fine_g: number; spot_eur: number; definition_version?: string | null };

/** Datumi na kojima se verzija definicije snimke promijenila (audit 03.10. T11). */
function definitionBreaks(points: HistoryPoint[]): string[] {
    const out: string[] = [];
    for (let i = 1; i < points.length; i++) {
        if ((points[i].definition_version ?? null) !== (points[i - 1].definition_version ?? null)) out.push(points[i].date);
    }
    return out;
}

type WarehouseCheck = {
    warehouse_code: string;
    warehouse_name: string;
    physical_qty: number;
    s2_qty: number;
    allocated_qty: number;
    l1_stanje_qty: number;
};

type Overview = {
    as_of: string;
    spot: { eur_per_gram: number; eur_per_troy_oz: number; spot_date: string | null };
    freshness: {
        stock_batch_id: number | null;
        stock_as_of: string | null;
        stock_age_days: number | null;
        open_orders_as_of: string | null;
        unknown_warehouses?: { code: string; name: string; rows: number }[];
        stale: boolean;
    };
    rows: Row[];
    signal: Signal;
    transit_reconcile: {
        snapshot_date: string | null;
        faros_qty_in_transit: number | null;
        bis_outbound_qty: number;
        bis_inbound_qty: number;
        bis_total_qty: number;
        difference: number | null;
    };
    double_count_check: {
        by_warehouse: WarehouseCheck[];
        overflow: Array<{ warehouse_code: string; item_code: string; stock_qty: number; reserved_qty: number; committed_qty: number }>;
        ok: boolean;
    };
    settings: { band_pct: number; window_days: number; h1_manual_eur: number; h1_manual_kg?: number };
    exposure_settings: { stale_days: number };
    can_edit: boolean;
};

const MANUAL_METRICS = ['p1', 'p2', 'v1', 'v2', 'v3', 'v4', 'h2'] as const;

const T = {
    hr: {
        title: 'Ukupna izlozenost zlatu',
        lead: 'Nadskup 40.61 — dodaje pohranu tudeg zlata (P1/P2, informativno), nase zlato izvan lagera (V1-V4), StoneX trading (H6) i zbrojni Z2.',
        coverage: 'Pokrivenost narudzbi',
        lager: 'Lager (izvan formule Z1)',
        result: 'Razlika',
        custody: 'Pohrana tudeg zlata (informativno, ne ulazi u Z2)',
        vault: 'Nase zlato izvan BIS lagera',
        hedge: 'StoneX trading dio',
        book: 'Knjigovodstveno EUR',
        fine: 'Fine XAU g',
        spot: 'Spot EUR',
        qty: 'Kom',
        item: 'Stavka',
        stale: 'Zaliha nije od danas ili je snimka otvorenih narudzbi starija od 24 h. Brojke kasne.',
        asOf: 'Faros zaliha',
        openOrders: 'Narudzbe',
        spotLabel: 'Spot',
        green: 'Unutar praga',
        red: 'Izvan praga',
        unknown: 'Nema baze za postotak',
        shortage: 'manjak',
        surplus: 'visak',
        flat: 'na nuli',
        days: 'dana prometa',
        settings: 'Postavke',
        band: 'Prag semafora (% od I1)',
        window: 'Prozor za prosjek narudzbi (dana)',
        h1: 'H1 rucno (EUR)',
        h1Kg: 'H1 rucno (kg, ima prednost pred EUR)',
        staleDays: 'Prag starosti rucnog unosa (dana)',
        save: 'Spremi',
        saved: 'Spremljeno.',
        reconcile: 'Sravnjenje robe na putu s Farosom',
        farosTotal: 'Faros ukupno na putu',
        bisOut: 'BIS S3 prema poslovnicama',
        bisIn: 'BIS T1 prema veleprodaji',
        diff: 'Razlika',
        check: 'Kontrola dvostrukog brojanja',
        checkOk: 'Nijedno skladiste ne broji istu robu dvaput.',
        checkBad: 'Rezervacije i alokacije prelaze fizicko stanje na ovim celijama:',
        physical: 'Fizicki',
        reserved: 'S2',
        allocated: 'Alocirano',
        free: 'L1 stanje',
        trend: 'Povijest',
        trendClose: 'Zatvori',
        trend30: '30 d',
        trend365: '365 d',
        trendEmpty: 'Nema jos snimljenih dana za ovu stavku.',
        trendLoading: 'Ucitavam...',
        pending: 'U pripremi (nema unosa)',
        staleFlag: 'Star unos',
        enteredBy: 'Unio',
        entryDate: 'Na dan',
        manualEntry: 'Rucni unos',
        metricLabel: 'Metrika',
        bookEurLabel: 'Iznos EUR',
        entryDateLabel: 'Na dan',
        noteLabel: 'Napomena',
        submitEntry: 'Zabiljezi',
        entrySaved: 'Unos zabiljezen.',
    },
    en: {
        title: 'Total gold exposure',
        lead: 'Superset of 40.61 — adds third-party custody (P1/P2, informational), our gold outside the lager (V1-V4), StoneX trading (H6) and total Z2.',
        coverage: 'Order coverage',
        lager: 'Lager (outside Z1)',
        result: 'Gap',
        custody: 'Third-party custody (informational, not in Z2)',
        vault: 'Our gold outside the BIS lager',
        hedge: 'StoneX trading portion',
        book: 'Book EUR',
        fine: 'Fine XAU g',
        spot: 'Spot EUR',
        qty: 'Pcs',
        item: 'Item',
        stale: 'Stock is not from today or the open orders snapshot is older than 24 h. Figures are late.',
        asOf: 'Faros stock',
        openOrders: 'Orders',
        spotLabel: 'Spot',
        green: 'Within band',
        red: 'Outside band',
        unknown: 'No base for percentage',
        shortage: 'shortage',
        surplus: 'surplus',
        flat: 'at zero',
        days: 'days of orders',
        settings: 'Settings',
        band: 'Traffic light band (% of I1)',
        window: 'Order average window (days)',
        h1: 'H1 manual (EUR)',
        h1Kg: 'H1 manual (kg, takes precedence over EUR)',
        staleDays: 'Manual entry stale threshold (days)',
        save: 'Save',
        saved: 'Saved.',
        reconcile: 'Goods in transit reconciled with Faros',
        farosTotal: 'Faros total in transit',
        bisOut: 'BIS S3 to branches',
        bisIn: 'BIS T1 to wholesale',
        diff: 'Difference',
        check: 'Double counting check',
        checkOk: 'No warehouse counts the same goods twice.',
        checkBad: 'Reservations and allocations exceed physical stock on these cells:',
        physical: 'Physical',
        reserved: 'S2',
        allocated: 'Allocated',
        free: 'L1 stock',
        trend: 'History',
        trendClose: 'Close',
        trend30: '30 d',
        trend365: '365 d',
        trendEmpty: 'No snapshot days recorded yet for this row.',
        trendLoading: 'Loading...',
        pending: 'Pending (no entry yet)',
        staleFlag: 'Stale entry',
        enteredBy: 'Entered by',
        entryDate: 'As of',
        manualEntry: 'Manual entry',
        metricLabel: 'Metric',
        bookEurLabel: 'Amount EUR',
        entryDateLabel: 'As of date',
        noteLabel: 'Note',
        submitEntry: 'Record',
        entrySaved: 'Entry recorded.',
    },
} as const;

/** Crno-bijela mini crta bez ovisnosti — jedna vrijednost (book_eur) kroz vrijeme. */
function Sparkline({ points }: { points: HistoryPoint[] }) {
    if (points.length < 2) return null;
    const w = 240;
    const h = 48;
    const pad = 4;
    const values = points.map((p) => p.book_eur);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const stepX = (w - pad * 2) / (points.length - 1);
    const coords = values.map((v, i) => {
        const x = pad + i * stepX;
        const y = pad + (h - pad * 2) * (1 - (v - min) / span);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const zeroY = pad + (h - pad * 2) * (1 - (0 - min) / span);
    const showZero = min < 0 && max > 0;
    return (
        <svg width={w} height={h} className="coverage-sparkline" role="img" aria-hidden="true">
            {showZero ? (
                <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="#999" strokeDasharray="2,2" />
            ) : null}
            <polyline points={coords.join(' ')} fill="none" stroke="#111" strokeWidth={1.5} />
        </svg>
    );
}

export function GoldExposureModule({
    token,
    locale,
    onBack,
}: {
    token: string;
    locale: Locale;
    onBack?: () => void;
}) {
    const t = T[locale];
    const [data, setData] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [band, setBand] = useState('');
    const [window_, setWindow] = useState('');
    const [h1, setH1] = useState('');
    const [h1Kg, setH1Kg] = useState('');
    const [staleDays, setStaleDays] = useState('');
    const [openTrend, setOpenTrend] = useState<string | null>(null);
    const [trendDays, setTrendDays] = useState<30 | 365>(30);
    const [trendPoints, setTrendPoints] = useState<HistoryPoint[] | null>(null);
    const [trendLoading, setTrendLoading] = useState(false);

    const [entryMetric, setEntryMetric] = useState<(typeof MANUAL_METRICS)[number]>('p1');
    const [entryBookEur, setEntryBookEur] = useState('');
    const [entryKg, setEntryKg] = useState('');
    const [entryDate, setEntryDate] = useState('');
    const [entryNote, setEntryNote] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await api<Overview>('/gold-exposure-v2/overview', token);
            setData(d);
            setBand(String(d.settings.band_pct));
            setWindow(String(d.settings.window_days));
            setH1(String(d.settings.h1_manual_eur));
            setH1Kg(String(d.settings.h1_manual_kg ?? 0));
            setStaleDays(String(d.exposure_settings.stale_days));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        void load();
    }, [load]);

    const saveSettings = async () => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await mutate('/gold-coverage-v2/settings', token, 'PUT', {
                band_pct: decimalInput(band),
                window_days: decimalInput(window_),
                h1_manual_eur: decimalInput(h1),
                h1_manual_kg: decimalInput(h1Kg),
            });
            await mutate('/gold-exposure-v2/settings', token, 'PUT', {
                stale_days: decimalInput(staleDays),
            });
            setNotice(t.saved);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const submitEntry = async () => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await mutate('/gold-exposure-v2/manual-entry', token, 'POST', {
                metric_key: entryMetric,
                ...(entryKg !== '' ? { kg: decimalInput(entryKg) } : { book_eur: decimalInput(entryBookEur) }),
                entry_date: entryDate || undefined,
                note: entryNote || undefined,
            });
            setNotice(t.entrySaved);
            setEntryBookEur('');
            setEntryKg('');
            setEntryNote('');
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const loadTrend = useCallback(
        async (metric: string, days: 30 | 365) => {
            setTrendLoading(true);
            setTrendPoints(null);
            try {
                const to = localIsoDate();
                const from = localIsoDate(new Date(Date.now() - days * 86400000));
                const res = await api<{ points: HistoryPoint[] }>(
                    `/gold-coverage/history?scope=40.62&metric=${encodeURIComponent(metric)}&from=${from}&to=${to}`,
                    token
                );
                setTrendPoints(res.points);
            } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
            } finally {
                setTrendLoading(false);
            }
        },
        [token]
    );

    const toggleTrend = (key: string) => {
        if (openTrend === key) {
            setOpenTrend(null);
            setTrendPoints(null);
            return;
        }
        setOpenTrend(key);
        void loadTrend(key, trendDays);
    };

    const changeTrendDays = (days: 30 | 365) => {
        setTrendDays(days);
        if (openTrend) void loadTrend(openTrend, days);
    };

    const groups = useMemo(() => {
        const out: Record<string, Row[]> = { coverage: [], lager: [], result: [], custody: [], vault: [], hedge: [] };
        (data?.rows ?? []).forEach((r) => out[r.group]?.push(r));
        return out;
    }, [data]);

    const rowByKey = useCallback((key: string) => data?.rows.find((r) => r.key === key), [data]);

    const formatFine = (grams: number) => {
        if (Math.abs(grams) >= 1000) return `${formatNumber(grams / 1000, locale)} kg`;
        return `${formatNumber(grams, locale)} g`;
    };

    if (loading) return <BrandSpinner />;
    if (error && !data) return <p className="error">{error}</p>;
    if (!data) return null;

    const signalText = (s: Signal) => {
        const dir = s.direction === 'shortage' ? t.shortage : s.direction === 'surplus' ? t.surplus : t.flat;
        const state = s.status === 'green' ? t.green : s.status === 'red' ? t.red : t.unknown;
        const pct = s.deviation_pct === null ? '-' : `${formatNumber(s.deviation_pct, locale)} %`;
        return `${state} (${pct} / ${formatNumber(s.band_pct, locale)} %), ${dir}`;
    };

    const renderRows = (rows: Row[], manual = false) =>
        rows.flatMap((r) => {
            const flags: string[] = [];
            if (r.pending) flags.push(t.pending);
            if (r.stale) flags.push(t.staleFlag);
            const flagsText = flags.length ? ` (${flags.join(', ')})` : '';
            const isTotal = r.key === 'z1' || r.key === 'z1b' || r.key === 'z2';
            const line = (
                <tr
                    key={r.key}
                    className={isTotal ? 'is-total' : undefined}
                    title={r.note ?? undefined}
                    style={isTotal ? { fontWeight: 600, borderTop: '2px solid #cbd5e1', background: '#f8fafc' } : undefined}
                >
                    <td>
                        {r.label}
                        {flagsText ? <span className="muted">{flagsText}</span> : null}
                        {manual && r.entry_date ? (
                            <span className="muted">
                                {' '}
                                — {t.entryDate}: {r.entry_date}
                                {r.entered_by ? ` (${t.enteredBy}: ${r.entered_by})` : ''}
                            </span>
                        ) : null}
                    </td>
                    <td className="num">{r.qty ? formatNumber(r.qty, locale) : '-'}</td>
                    <td className="num">{formatFine(r.fine_g)}</td>
                    <td className="num">{formatMoney(r.book_eur, locale)}</td>
                    <td className="num">{formatMoney(r.spot_eur, locale)}</td>
                    <td className="num">
                        <button type="button" className="link-btn" onClick={() => toggleTrend(r.key)}>
                            {t.trend}
                        </button>
                    </td>
                </tr>
            );
            if (openTrend !== r.key) return [line];
            return [
                line,
                <tr key={`${r.key}-trend`}>
                    <td colSpan={6} className="coverage-trend-cell">
                        <div className="filters flat">
                            <button type="button" onClick={() => changeTrendDays(30)} disabled={trendDays === 30}>
                                {t.trend30}
                            </button>
                            <button type="button" onClick={() => changeTrendDays(365)} disabled={trendDays === 365}>
                                {t.trend365}
                            </button>
                            <button type="button" onClick={() => toggleTrend(r.key)}>
                                {t.trendClose}
                            </button>
                        </div>
                        {trendLoading ? (
                            <p className="muted">{t.trendLoading}</p>
                        ) : trendPoints && trendPoints.length >= 2 ? (
                            <>
                                <Sparkline points={trendPoints} />
                                {definitionBreaks(trendPoints).length > 0 ? (
                                    <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                                        {locale === 'en' ? 'Definition change (not a business change) on: ' : 'Promjena definicije izračuna (nije promjena poslovanja) na dan: '}
                                        {definitionBreaks(trendPoints).join(', ')}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <p className="muted">{t.trendEmpty}</p>
                        )}
                    </td>
                </tr>,
            ];
        });

    const groupHead = (label: string) => (
        <tr className="group-head" style={{ background: '#eef2f7' }}>
            <th colSpan={6} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.02em', color: '#475569' }}>
                {label}
            </th>
        </tr>
    );

    const rec = data.transit_reconcile;
    const z1 = rowByKey('z1');
    const z1b = rowByKey('z1b');
    const i1 = rowByKey('i1');
    const z2 = rowByKey('z2');
    const raspolozivoSpotEur =
        (rowByKey('s1')?.spot_eur ?? 0) +
        (rowByKey('s2')?.spot_eur ?? 0) +
        (rowByKey('s2b')?.spot_eur ?? 0) +
        (rowByKey('s3')?.spot_eur ?? 0) +
        (rowByKey('s4')?.spot_eur ?? 0);
    const z1Color = data.signal.status === 'green' ? '#16a34a' : data.signal.status === 'red' ? '#dc2626' : '#64748b';
    const z1Bg = data.signal.status === 'green' ? '#f0fdf4' : data.signal.status === 'red' ? '#fef2f2' : '#f8fafc';
    const z1bPositive = (z1b?.spot_eur ?? 0) >= 0;
    const z2Positive = (z2?.spot_eur ?? 0) >= 0;

    return (
        <div className="card admin-block coverage-v2-module">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <Breadcrumb
                        items={[
                            { label: locale === 'hr' ? 'Skladiste' : 'Warehouse', onClick: onBack },
                            { label: `40.62 ${t.title}` },
                        ]}
                    />
                    <div className="bi-module-hero" style={{ marginTop: '0.5rem' }}>
                        <div className="bi-module-hero-icon">🌍</div>
                        <div className="bi-module-hero-text">
                            <h1>40.62 · {t.title}</h1>
                            <p className="muted">{t.lead}</p>
                        </div>
                    </div>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
                    🔄 {locale === 'hr' ? 'Osvježi' : 'Refresh'}
                </button>
            </div>

            <div
                style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '0.75rem 1.25rem',
                    margin: '1.25rem 0',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    fontSize: '0.875rem',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                    <div>
                        <span className="muted">{t.spotLabel}: </span>
                        <strong>{formatMoney(data.spot.eur_per_gram, locale)}/g</strong>
                    </div>
                    <div>
                        <span className="muted">{t.asOf}: </span>
                        <span>{data.freshness.stock_as_of ?? '-'}</span>
                        {data.freshness.stock_age_days !== null ? <span className="muted"> ({data.freshness.stock_age_days} d)</span> : null}
                    </div>
                    <div>
                        <span className="muted">{t.openOrders}: </span>
                        <span>{data.freshness.open_orders_as_of ?? '-'}</span>
                    </div>
                </div>
            </div>
            {data.freshness.unknown_warehouses?.length ? (
                <p className="card-alert card-alert-danger error" style={{ padding: '0.5rem 0 0.5rem 1rem' }}>
                    {locale === 'en'
                        ? 'Warehouses in the stock snapshot without a row in admin 128, counted as branches: '
                        : 'Skladista u snimci zalihe bez retka u adminu 128, racunaju se kao poslovnica: '}
                    {data.freshness.unknown_warehouses.map((w) => `${w.code} ${w.name}`).join(', ')}
                </p>
            ) : null}
            {data.freshness.stale ? <p className="card-alert card-alert-danger error" style={{ padding: '0.5rem 0 0.5rem 1rem' }}>{t.stale}</p> : null}
            {error ? <p className="error">{error}</p> : null}
            {notice ? <p className="muted">{notice}</p> : null}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="card" style={{ padding: '1.25rem', borderLeft: `4px solid ${z1Color}`, background: z1Bg }}>
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {z1?.label ?? 'Z1'}
                    </p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: z1Color, fontSize: '1.75rem' }}>
                        {formatMoney(z1?.spot_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>{signalText(data.signal)}</p>
                </div>

                <div
                    className="card"
                    style={{ padding: '1.25rem', borderLeft: `4px solid ${z1bPositive ? '#16a34a' : '#dc2626'}`, background: z1bPositive ? '#f0fdf4' : '#fef2f2' }}
                >
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {z1b?.label ?? 'Z1b'}
                    </p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: z1bPositive ? '#15803d' : '#b91c1c', fontSize: '1.75rem' }}>
                        {formatMoney(z1b?.spot_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {locale === 'hr' ? 'Bez H1 StoneX pokrica' : 'Without H1 StoneX cover'}
                    </p>
                </div>

                <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #d97706', background: '#fffbeb' }}>
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {locale === 'hr' ? 'I1 NEISPORUČENE NARUDŽBE' : 'I1 UNDELIVERED ORDERS'}
                    </p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: '#b45309', fontSize: '1.75rem' }}>
                        {formatMoney(i1?.book_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {i1?.order_count ?? 0} {locale === 'hr' ? 'nar.' : 'ord.'} ·{' '}
                        {formatMoney(i1?.remaining_eur ?? 0, locale)} {locale === 'hr' ? 'nenaplaćeno' : 'unpaid'}
                    </p>
                </div>

                <div
                    className="card"
                    style={{ padding: '1.25rem', borderLeft: `4px solid ${z2Positive ? '#16a34a' : '#dc2626'}`, background: z2Positive ? '#f0fdf4' : '#fef2f2' }}
                >
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {z2?.label ?? 'Z2'}
                    </p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: z2Positive ? '#15803d' : '#b91c1c', fontSize: '1.75rem' }}>
                        {formatMoney(z2?.spot_eur ?? 0, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {locale === 'hr' ? 'Ukupna izlozenost (nadskup 40.61)' : 'Total exposure (superset of 40.61)'}
                    </p>
                </div>

                <div className="card" style={{ padding: '1.25rem', borderLeft: '4px solid #2563eb', background: '#eff6ff' }}>
                    <p className="eyebrow" style={{ margin: 0, color: '#64748b' }}>
                        {locale === 'hr' ? 'RASPOLOŽIVO S1+S2+S2b+S3+S4' : 'AVAILABLE S1+S2+S2b+S3+S4'}
                    </p>
                    <h2 style={{ margin: '0.4rem 0 0.2rem', color: '#1d4ed8', fontSize: '1.75rem' }}>
                        {formatMoney(raspolozivoSpotEur, locale)}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>{locale === 'hr' ? 'Spot EUR' : 'Spot EUR'}</p>
                </div>
            </div>

            <h3 style={{ margin: '0 0 0.75rem' }}>{locale === 'hr' ? 'Glavna tablica' : 'Main table'}</h3>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>{t.item}</th>
                        <th className="num">{t.qty}</th>
                        <th className="num">{t.fine}</th>
                        <th className="num">{t.book}</th>
                        <th className="num">{t.spot}</th>
                        <th className="num">{t.trend}</th>
                    </tr>
                </thead>
                <tbody>
                    {groupHead(t.coverage)}
                    {renderRows(groups.coverage)}
                    {groupHead(t.lager)}
                    {renderRows(groups.lager)}
                    {groupHead(t.result)}
                    {renderRows(groups.result)}
                    {groupHead(t.custody)}
                    {renderRows(groups.custody, true)}
                    {groupHead(t.vault)}
                    {renderRows(groups.vault, true)}
                    {groupHead(t.hedge)}
                    {renderRows(groups.hedge, true)}
                </tbody>
            </table>

            <p className={data.signal.status === 'red' ? 'error' : 'muted'}>
                Z1: {signalText(data.signal)}
                {data.signal.days !== null ? ` · ${formatNumber(data.signal.days, locale)} ${t.days}` : ''}
            </p>

            <CoverageDayView token={token} locale={locale} scope="40.62" labels={Object.fromEntries(data.rows.map((r) => [r.key, r.label]))} />

            <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{t.reconcile}</h3>
                <table className="data-table">
                    <tbody>
                        <tr>
                            <td>{t.farosTotal}</td>
                            <td className="num">{rec.faros_qty_in_transit === null ? '-' : formatNumber(rec.faros_qty_in_transit, locale)}</td>
                        </tr>
                        <tr>
                            <td>{t.bisOut}</td>
                            <td className="num">{formatNumber(rec.bis_outbound_qty, locale)}</td>
                        </tr>
                        <tr>
                            <td>{t.bisIn}</td>
                            <td className="num">{formatNumber(rec.bis_inbound_qty, locale)}</td>
                        </tr>
                        <tr>
                            <td>{t.diff}</td>
                            <td className="num">{rec.difference === null ? '-' : formatNumber(rec.difference, locale)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{t.check}</h3>
                {data.double_count_check.ok ? (
                    <p className="card-alert card-alert-info" style={{ margin: '0 0 0.75rem' }}>{t.checkOk}</p>
                ) : (
                    <>
                        <p className="card-alert card-alert-danger error" style={{ margin: '0 0 0.75rem' }}>{t.checkBad}</p>
                        <ul>
                            {data.double_count_check.overflow.map((o) => (
                                <li key={`${o.warehouse_code}-${o.item_code}`}>
                                    {o.warehouse_code} / {o.item_code}: {formatNumber(o.stock_qty, locale)} vs{' '}
                                    {formatNumber(o.reserved_qty + o.committed_qty, locale)}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>{locale === 'hr' ? 'Poslovnica' : 'Branch'}</th>
                            <th className="num">{t.physical}</th>
                            <th className="num">{t.reserved}</th>
                            <th className="num">{t.allocated}</th>
                            <th className="num">{t.free}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.double_count_check.by_warehouse.map((w) => (
                            <tr key={w.warehouse_code}>
                                <td>
                                    {w.warehouse_code} {w.warehouse_name}
                                </td>
                                <td className="num">{formatNumber(w.physical_qty, locale)}</td>
                                <td className="num">{formatNumber(w.s2_qty, locale)}</td>
                                <td className="num">{formatNumber(w.allocated_qty, locale)}</td>
                                <td className="num">{formatNumber(w.l1_stanje_qty, locale)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.75rem' }}>{locale === 'en' ? 'StoneX trades from Hedging (H5 hedge feeds H1, H6 trading)' : 'StoneX nalozi iz Hedginga (H5 hedge puni H1, H6 trading)'}</h3>
                <HedgeTradesPanel locale={locale} token={token} canEdit={!!data.can_edit} onChanged={() => void load()} />
            </div>

            {data.can_edit ? (
                <>
                    <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.25rem' }}>
                        <h3 style={{ margin: '0 0 0.75rem' }}>{t.manualEntry}</h3>
                        <div className="filters flat">
                            <label>
                                {t.metricLabel}
                                <select value={entryMetric} onChange={(e) => setEntryMetric(e.target.value as (typeof MANUAL_METRICS)[number])} disabled={busy}>
                                    {MANUAL_METRICS.map((m) => (
                                        <option key={m} value={m}>
                                            {m.toUpperCase()}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                {t.bookEurLabel}
                                <input value={entryBookEur} onChange={(e) => setEntryBookEur(e.target.value)} disabled={busy} />
                            </label>
                            <label>
                                {locale === 'en' ? 'or kg fine (EUR at spot)' : 'ili kg finog (EUR po spotu)'}
                                <input value={entryKg} onChange={(e) => setEntryKg(e.target.value)} disabled={busy} />
                            </label>
                            <label>
                                {t.entryDateLabel}
                                <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} disabled={busy} />
                            </label>
                            <label>
                                {t.noteLabel}
                                <input value={entryNote} onChange={(e) => setEntryNote(e.target.value)} disabled={busy} />
                            </label>
                            <button type="button" onClick={() => void submitEntry()} disabled={busy || (entryBookEur === '' && entryKg === '')}>
                                {t.submitEntry}
                            </button>
                        </div>
                    </div>

                    <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', marginTop: '1.25rem' }}>
                        <h3 style={{ margin: '0 0 0.75rem' }}>{t.settings}</h3>
                        <div className="filters flat">
                            <label>
                                {t.band}
                                <input value={band} onChange={(e) => setBand(e.target.value)} disabled={busy} />
                            </label>
                            <label>
                                {t.window}
                                <input value={window_} onChange={(e) => setWindow(e.target.value)} disabled={busy} />
                            </label>
                            <label>
                                {t.h1}
                                <input value={h1} onChange={(e) => setH1(e.target.value)} disabled={busy} />
                            </label>
                            <label>
                                {t.h1Kg}
                                <input value={h1Kg} onChange={(e) => setH1Kg(e.target.value)} disabled={busy} />
                            </label>
                            <label>
                                {t.staleDays}
                                <input value={staleDays} onChange={(e) => setStaleDays(e.target.value)} disabled={busy} />
                            </label>
                            <button type="button" onClick={() => void saveSettings()} disabled={busy}>
                                {t.save}
                            </button>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
}
